import {
  CollapseButton,
  CollapseSection,
  EditableTypography,
  FormattedPrice,
  isImageOr3dModel,
  isPdfOrFile,
  ModalOpenButton,
  processAttachment,
  TextWithOverflow,
  useScreenWidth,
} from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  List,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useTranslate } from "@tolgee/react";
import { noop, partition } from "lodash-es";
import React from "react";
import { AskWhom, PermissionName } from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { GalleryRow } from "../../commons/images/GalleryRow.js";
import { useProjectRequirementContext } from "../../main-routes/projects/ProjectRequirementContext.js";
import {
  ALL_KEY,
  ExpandedStore,
} from "../../trees-virtual/hooks/useExpandedStore.js";
import { FileListItemButton } from "../../commons/files/FileListItemButton.js";
import { FilesBoxTable } from "../attachments/FilesBoxTable.js";
import { useModifyItemCalculation } from "../doc-data-grid/useModifyItemCalculation.js";
import { useModifyItemInfo } from "../doc-data-grid/useModifyItemInfo.js";
import { useModifyItemProduct } from "../doc-data-grid/useModifyItemProduct.js";
import { ContingencyCheckbox } from "../doc-items/fields/ContingencyCheckbox.js";
import { DecisionOptionRadioOrCheckbox } from "../doc-items/fields/DecisionOptionRadioOrCheckbox.js";
import { SubitemsOptionsNoneSection } from "../doc-items/fields/SubitemsOptionsNoneSection.js";
import { getViewerDecisionRole } from "../doc-items/helpers.js";
import { useDecisionItemMutations } from "../doc-items/hooks/useDecisionItemMutations.js";
import { useQuantityUnits } from "../doc-items/useQuantityUnits.js";
import { DocumentActorData } from "../documents/DocumentActorData.js";
import { DocumentPreviewItems } from "../documents/DocumentPreviewItems.js";
import { Fields } from "../documents/DocumentPreviewItemsTypes.js";
import { ItemWizardButton } from "../projects/requirement-trees.js";
import { EditableQuantityCell } from "../quotes/preview/EditableQuantityCell.js";
import { M1Inputs } from "./M1Inputs.js";
import { RequirementClientInputsBox } from "./RequirementClientInputsBox.js";
import { RequirementPreviewHeader } from "./preview/RequirementPreviewHeader.js";
import {
  requirementItemHasDescription,
  RequirementPreviewItemDescription,
} from "./preview/RequirementPreviewItemDescription.js";
import { RequirementDocumentActorsModal } from "./RequirementDocumentActorsModal.js";
import {
  RequirementPreview_ItemFragment,
  RequirementPreview_RequirementFragment,
} from "./RequirementPreview.generated.js";

interface Props<CanFinalize extends boolean> {
  navigateToItem?: (item: RequirementPreview_ItemFragment) => void;
  selectedItemId?: string | null;
  requirement: RequirementPreview_RequirementFragment;
  // The project the requirement is viewed in (route context). May differ from
  // requirement.projectId when the requirement is shared from another project.
  projectId: string;
  canFinalize: CanFinalize;
  canEdit: boolean;
  expandedStore?: ExpandedStore;
  expandedItemIds: string[] | undefined;
  refetch?: () => Promise<unknown>;
  container?: HTMLElement | null;
}

export const RequirementPreview = <CanFinalize extends boolean>({
  navigateToItem,
  selectedItemId,
  requirement,
  projectId,
  canFinalize,
  canEdit,
  expandedStore,
  expandedItemIds,
  refetch,
  container,
}: Props<CanFinalize>) => {
  const viewer = useUserData().currentUser!;
  const { isMinTablet, isMinDesktop } = useScreenWidth();

  const { t } = useTranslate(["Quote", "Decisions", "Global"]);

  const allDocItems = requirement.items;

  const rootItem = allDocItems.find(item => item.isRootItem);

  const [images, files] = partition(
    (rootItem?.attachments ?? [])
      .filter(attachment => attachment.clientVisibility)
      .map(processAttachment),
    isImageOr3dModel
  );

  const viewerIsOwner =
    viewer.organisation.id === requirement.owningSystemOrganisationId;

  const editable = canEdit && viewerIsOwner;

  const { modifyItemInfo } = useModifyItemInfo({
    projectId: requirement.projectId,
    docId: requirement.id,
  });
  const { modifyItemProduct } = useModifyItemProduct({
    projectId: requirement.projectId,
    docId: requirement.id,
  });

  const viewerDecisionRole = getViewerDecisionRole(requirement.actors);

  const extraActors = React.useMemo(
    () =>
      requirement.actors.filter(actor => !["CONTRACTEE"].includes(actor.type)),
    [requirement.actors]
  );

  const { hasItemAnyQuestions, refetchWizardQuestions } =
    useProjectRequirementContext();

  return (
    <>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          width: "100%",
        }}
      >
        <RequirementPreviewHeader requirement={requirement} />

        {extraActors.length > 0 && (
          <Paper sx={{ position: "relative", padding: 2, paddingRight: 2 }}>
            <Stack
              direction="row"
              spacing={4}
              sx={{
                alignItems: "flex-start",
                justifyItems: "space-between",
              }}
            >
              <Stack
                direction="row"
                spacing={4}
                sx={{
                  flex: 1,
                }}
              >
                {extraActors.map(actor => (
                  <DocumentActorData actorType={actor.type} actor={actor} />
                ))}
              </Stack>
              <RestrictedByDocumentPermissionWithDebug
                permission="requirement_actors_update"
                document={requirement}
              >
                <ModalOpenButton
                  Modal={RequirementDocumentActorsModal}
                  modalProps={{
                    projectId: requirement.projectId,
                    requirementId: requirement.id,
                  }}
                >
                  <IconButton color="secondary" size="small">
                    <EditIcon />
                  </IconButton>
                </ModalOpenButton>
              </RestrictedByDocumentPermissionWithDebug>
            </Stack>
          </Paper>
        )}

        <Paper>
          <Box
            sx={{
              padding: 2,
            }}
          >
            {canFinalize && rootItem && hasItemAnyQuestions(rootItem.id) && (
              <RestrictedByDocumentPermissionWithDebug
                permission="requirement_decisions_decide"
                document={requirement}
              >
                <div
                  style={{
                    float: "right",
                    display: "flex",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <ItemWizardButton
                    projectId={requirement.projectId}
                    requirement={requirement}
                    item={rootItem}
                    refetchAfterApplyItemAction={refetchWizardQuestions}
                  />
                </div>
              </RestrictedByDocumentPermissionWithDebug>
            )}
            {editable && rootItem ? (
              <EditableTypography
                variant="h2"
                value={requirement.title}
                allowEmpty={false}
                placeholder={t("Title", { ns: "Global" })}
                onChange={async value => {
                  await modifyItemInfo(rootItem.id, { title: value });
                  await refetch?.();
                }}
              />
            ) : (
              <Typography variant="h2">{requirement.title}</Typography>
            )}
          </Box>
        </Paper>

        {requirement.m1 ? <M1Inputs m1={requirement.m1} /> : null}

        {rootItem &&
          (images.length > 0 || requirementItemHasDescription(rootItem)) && (
            <Paper>
              <Stack
                direction={"column"}
                spacing={2}
                sx={{
                  padding: 2,
                }}
              >
                <GalleryRow
                  images={images}
                  maxItems={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
                />

                <RequirementPreviewItemDescription
                  item={rootItem}
                  columns={isMinDesktop ? 4 : isMinTablet ? 2 : 1}
                  editing={
                    editable
                      ? {
                          onChangeDescription: async value => {
                            await modifyItemInfo(rootItem.id, {
                              description: value,
                            });
                            await refetch?.();
                          },
                          onChangeProductTitle: async value => {
                            await modifyItemProduct(rootItem.id, {
                              productTitle: value,
                            });
                            await refetch?.();
                          },
                          onChangeProductDescription: async value => {
                            await modifyItemProduct(rootItem.id, {
                              productDescription: value,
                            });
                            await refetch?.();
                          },
                        }
                      : undefined
                  }
                />
              </Stack>
            </Paper>
          )}

        <RequirementPreviewItems
          requirement={requirement}
          viewerIsOwner={viewerIsOwner}
          viewerDecisionRole={viewerDecisionRole}
          projectId={
            /* prettier-ignore */
            (canFinalize ? requirement.projectId : null) as (CanFinalize extends true ? string : null)
          }
          canFinalize={canFinalize}
          editable={editable}
          allDocItems={allDocItems}
          navigateToItem={navigateToItem}
          selectedItemId={selectedItemId}
          expandedStore={expandedStore}
          expandedItemIds={expandedItemIds}
          refetch={refetch}
          container={container}
          hasItemAnyQuestions={hasItemAnyQuestions}
          refetchWizardQuestions={refetchWizardQuestions}
        />

        <Paper>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              p: 2,
            }}
          >
            <Typography variant="h2" component="div">
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {t("Subtotal", {
                    ns: "Quote",
                  })}
                </span>
                <FormattedPrice isPlaceholder={true} />
              </Stack>
            </Typography>
            <Typography variant="h1" component="div">
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {t("Total", {
                    ns: "Quote",
                  })}
                </span>
                <FormattedPrice isPlaceholder={true} />
              </Stack>
            </Typography>
          </Stack>
        </Paper>

        {files.length > 0 && (
          <FilesBoxTable
            title={t("Attachments", { ns: "Quote" })}
            attachments={files}
            canEdit={false}
            gridColumns={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
          />
        )}

        <RequirementClientInputsBox
          requirementId={requirement.id}
          projectId={projectId}
        />
      </Stack>
    </>
  );
};

export const RequirementPreviewItems = <CanFinalize extends boolean>({
  requirement,
  viewerDecisionRole,
  projectId,
  canFinalize,
  editable = false,
  navigateToItem,
  selectedItemId,
  allDocItems,
  expandedStore,
  expandedItemIds,
  refetch,
  container,
  viewerIsOwner,
  hasItemAnyQuestions,
  refetchWizardQuestions,
}: {
  projectId: CanFinalize extends true ? string : null;
  requirement: { id: string; viewerPermissions: PermissionName[] };
  viewerDecisionRole: AskWhom | undefined;
  canFinalize: CanFinalize;
  editable?: boolean;
  navigateToItem?: (item: RequirementPreview_ItemFragment) => void;
  selectedItemId?: string | null;
  allDocItems: RequirementPreview_ItemFragment[];
  expandedStore?: ExpandedStore;
  expandedItemIds: string[] | undefined;
  refetch?: () => Promise<unknown>;
  container?: HTMLElement | null;
  viewerIsOwner: boolean;
  hasItemAnyQuestions: (itemId: string) => boolean;
  refetchWizardQuestions: () => Promise<void>;
}) => {
  const { t } = useTranslate(["Quote", "Decisions", "Global"]);

  const { quantityUnitLabels } = useQuantityUnits();

  const { modifyItemInfo } = useModifyItemInfo({
    projectId,
    docId: requirement.id,
  });
  const { modifyItemProduct } = useModifyItemProduct({
    projectId,
    docId: requirement.id,
  });
  const { modifyItemCalculation } = useModifyItemCalculation({
    projectId,
    docId: requirement.id,
  });

  const {
    handleDecisionPreselectionChange,
    handleContingencyPreselectionChange,
    handleFinalizeContingencyDecision,
    handleFinalizeSubitemDecision,
    canForceFinalizeSubitemDecision,
    canFinalizeContingencyDecision,
    loading,
  } = useDecisionItemMutations<RequirementPreview_ItemFragment>(
    projectId,
    requirement.id,
    canFinalize,
    expandedItemIds,
    refetchWizardQuestions
  );

  const collapseItemAndChildrenRecursively = React.useCallback(
    (item: RequirementPreview_ItemFragment) => {
      if (!expandedStore) return;
      const toCollapseIds = allDocItems
        .filter(i => i.pathForPdf.startsWith(item.pathForPdf))
        .map(i => i.id);
      const toExpandItemIds = expandedStore.areAllItemsExpanded
        ? allDocItems
            .filter(i => i.hasChildren && !toCollapseIds.includes(i.id))
            .map(i => i.id)
        : [];
      expandedStore.setExpandedState(
        Object.fromEntries([
          [ALL_KEY, false],
          ...toExpandItemIds.map(id => [id, true]),
          ...toCollapseIds.map(id => [id, false]),
        ])
      );
    },
    [expandedStore, allDocItems]
  );

  const fields = React.useMemo(
    (): Fields<RequirementPreview_ItemFragment> => ({
      CollapseButton: {
        render: item => {
          if (!expandedStore) return null;
          const expanded = expandedStore.isItemExpanded(item.id);
          return item.hasChildren ? (
            <CollapseButton
              aria-label={expanded ? "Collapse" : "Expand"}
              isExpanded={expanded}
              setIsExpanded={newExpanded => {
                if (newExpanded) {
                  expandedStore.setItemExpanded(item.id, newExpanded);
                } else {
                  collapseItemAndChildrenRecursively(item);
                }
              }}
            />
          ) : null;
        },
      },
      NonBindingCaption: {
        render: item => {
          if (item.type === "paid" && item.binding === "non_binding") {
            return t("Non-Binding item", {
              ns: "Quote",
            });
          }

          return null;
        },
      },
      ContingencyOptionalCaption: {
        render: item => {
          if (item.decisionIsContingentItem) {
            const captions: string[] = [];
            captions.push(
              t("Optional item", {
                ns: "Quote",
              })
            );

            if (item.decisionContingentItemPreselection) {
              captions.push(
                t("Recommended", {
                  ns: "Quote",
                })
              );
            }

            return captions.join(" • ");
          }

          return null;
        },
      },
      ContingencyOptionsButtons: {
        render: item => {
          return item.decisionIsContingentItem ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
              otherwise={
                <ContingencyCheckbox
                  item={item}
                  canEdit={false}
                  disabled={true}
                  handleContingencyPreselectionChange={noop}
                />
              }
            >
              <ContingencyCheckbox
                item={item}
                loading={loading}
                canEdit={true}
                handleContingencyPreselectionChange={async (...args) => {
                  await handleContingencyPreselectionChange(...args);
                  await refetch?.();
                }}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      ContingencyFinalizeButton: {
        render: item => {
          return item.decisionIsContingentItem && canFinalize ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
            >
              <Button
                disabled={loading || !canFinalizeContingencyDecision(item)}
                size="extra-small"
                variant="contained"
                color="primary"
                onClick={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleFinalizeContingencyDecision(item.id);
                  await refetch?.();
                }}
              >
                {t("Resolve", {
                  ns: "Quote",
                })}
              </Button>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionOptionButton: {
        render: (item, parent) => {
          return parent?.type === "decision" &&
            !item.decisionOptionElimination ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
              otherwise={
                <DecisionOptionRadioOrCheckbox
                  docId={requirement.id}
                  projectId={projectId}
                  item={item}
                  disabled={true}
                  expandedItemIds={expandedItemIds}
                />
              }
            >
              <DecisionOptionRadioOrCheckbox
                docId={requirement.id}
                projectId={projectId}
                item={item}
                disabled={!canFinalize || loading}
                expandedItemIds={expandedItemIds}
                refetch={refetch}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionNoneOptionButton: {
        render: item => {
          return item.decisionBehaviorOfSubitems === "SELECT_ONE" ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
              otherwise={
                <SubitemsOptionsNoneSection
                  item={item}
                  canEdit={false}
                  disabled={true}
                  handleDecisionPreselectionChange={noop}
                />
              }
            >
              <SubitemsOptionsNoneSection
                item={item}
                loading={loading}
                canEdit={true}
                handleDecisionPreselectionChange={async (...args) => {
                  await handleDecisionPreselectionChange(...args);
                  await refetch?.();
                }}
                disabled={!canFinalize || loading}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      DecisionButton: {
        render: item =>
          canFinalize && projectId && hasItemAnyQuestions(item.id) ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
            >
              <ItemWizardButton
                projectId={projectId}
                requirement={requirement}
                item={item}
                refetchAfterApplyItemAction={refetchWizardQuestions}
              />
            </RestrictedByDocumentPermissionWithDebug>
          ) : null,
      },
      DecisionFinalizeButton: {
        render: item => {
          return item.type === "decision" && canFinalize ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="requirement_decisions_decide"
              document={requirement}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  flexWrap: "wrap",
                }}
              >
                <Button
                  disabled={loading || !canForceFinalizeSubitemDecision(item)}
                  size="extra-small"
                  variant="contained"
                  color="primary"
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (item.decisionIsContingentItem) {
                      if (item.decisionContingentItemPreselection !== true) {
                        await handleContingencyPreselectionChange(
                          item.id,
                          true
                        );
                      }
                      await handleFinalizeContingencyDecision(item.id);
                    }

                    await handleFinalizeSubitemDecision(item.id);
                    await refetch?.();
                  }}
                >
                  {t("Finalize decision", {
                    ns: "Quote",
                  })}
                </Button>
              </Stack>
            </RestrictedByDocumentPermissionWithDebug>
          ) : null;
        },
      },
      Pos: {
        render: item => <TextWithOverflow>{item.pathForPdf}</TextWithOverflow>,
      },
      Title: {
        render: item => {
          if (item.isRootItem) return null;
          if (!editable) {
            // No `<Typography variant="h3">` wrapper — the title inherits
            // typography from the surrounding column / row context, matching
            // the editable branch below and the quote preview pattern.
            return <Box sx={{ wordBreak: "break-word" }}>{item.title}</Box>;
          }
          return (
            <NoPropagateBox preventDefault={false}>
              <EditableTypography
                variant="inherit"
                value={item.title}
                allowEmpty={false}
                sx={{ wordBreak: "break-word", mr: 1 }}
                placeholder={t("Item title", { ns: "Quote" })}
                onChange={async value => {
                  await modifyItemInfo(item.id, { title: value });
                  await refetch?.();
                }}
              />
            </NoPropagateBox>
          );
        },
      },
      Description: {
        render: item => {
          if (!requirementItemHasDescription(item)) return null;
          if (item.isRootItem) return null;
          return (
            <RequirementPreviewItemDescription
              item={item}
              columns={3}
              editing={
                editable
                  ? {
                      onChangeDescription: async value => {
                        await modifyItemInfo(item.id, { description: value });
                        await refetch?.();
                      },
                      onChangeProductTitle: async value => {
                        await modifyItemProduct(item.id, {
                          productTitle: value,
                        });
                        await refetch?.();
                      },
                      onChangeProductDescription: async value => {
                        await modifyItemProduct(item.id, {
                          productDescription: value,
                        });
                        await refetch?.();
                      },
                    }
                  : undefined
              }
            />
          );
        },
      },
      Images: {
        render: item => {
          const images = item.attachments
            .filter(a => a.clientVisibility)
            .map(processAttachment)
            .filter(isImageOr3dModel);
          return images.length > 0 ? (
            <GalleryRow
              images={images}
              maxItems={4}
              style={item.level > 2 ? { maxWidth: "300px" } : undefined}
            />
          ) : null;
        },
      },
      Attachments: {
        render: item => {
          const files = item.attachments
            .filter(a => a.clientVisibility)
            .map(processAttachment)
            .filter(isPdfOrFile);
          return files.length > 0 ? (
            <CollapseSection
              title={t("Attachments", { ns: "Quote" })}
              titleVariant="body2"
              titleColor="secondary"
              iconButtonColor="secondary"
              sx={{ minHeight: "auto" }}
              isInitiallyExpanded={false}
              collapseTimeout={0}
            >
              <List
                disablePadding
                dense
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {files.map(attachment => (
                  <FileListItemButton
                    key={attachment.id}
                    file={attachment}
                    sx={{ minWidth: "200px", maxWidth: "320px", width: "auto" }}
                  />
                ))}
              </List>
            </CollapseSection>
          ) : null;
        },
      },
      Quantity: {
        render: item => {
          const { proposedCalculation } = item;

          // No `projectId` gate — `projectId` is null when `canFinalize` is
          // false, but inline editing only depends on `editable` (matching the
          // Title / Description editors) and the mutation hooks accept
          // `projectId: string | null`.
          if (editable && proposedCalculation) {
            return (
              <EditableQuantityCell
                projectId={projectId}
                docId={requirement.id}
                itemId={item.id}
                itemType={item.type}
                decisionBehaviorOfSubitems={item.decisionBehaviorOfSubitems}
                quantity={proposedCalculation.quantity}
                quantityUnit={proposedCalculation.quantityUnit}
                timePerUnit={proposedCalculation.timePerUnit}
                quantityExpression={
                  item.attributeExpressions.find(
                    e => e.attribute === "estimatedQuantity"
                  )?.expr
                }
                isTimeAndMaterialContract={false}
                onCommitQuantity={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedQuantity: value,
                  });
                  await refetch?.();
                }}
                onCommitUnit={async value => {
                  await modifyItemCalculation(item.id, {
                    quantityUnit: value,
                  });
                  await refetch?.();
                }}
                onCommitTime={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedTime: value,
                  });
                  await refetch?.();
                }}
                onExpressionChange={async () => {
                  await refetch?.();
                }}
              />
            );
          }

          if (item.type === "section") {
            if (proposedCalculation && proposedCalculation.quantity !== 1) {
              return (
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  {proposedCalculation
                    ? `${proposedCalculation.quantity}`
                    : "0"}
                </Box>
              );
            }
          }

          if (item.type === "paid") {
            return (
              <Box
                sx={{
                  display: "flex",
                }}
              >
                {proposedCalculation
                  ? `${proposedCalculation.quantity} ${
                      quantityUnitLabels[proposedCalculation.quantityUnit]
                    }`
                  : "0"}
              </Box>
            );
          }

          return null;
        },
      },
      UnitPrice: {
        render: () => null,
      },
      Subtotal: {
        render: (item, parent) => {
          return !parent?.isRootItem ? <FormattedPrice isPlaceholder /> : null;
        },
      },
      TotalPrice: {
        render: (item, parent) => {
          return parent?.isRootItem ? <FormattedPrice isPlaceholder /> : null;
        },
      },
    }),
    [
      expandedStore,
      collapseItemAndChildrenRecursively,
      t,
      requirement,
      loading,
      handleContingencyPreselectionChange,
      refetch,
      canFinalize,
      canFinalizeContingencyDecision,
      handleFinalizeContingencyDecision,
      projectId,
      expandedItemIds,
      handleDecisionPreselectionChange,
      hasItemAnyQuestions,
      refetchWizardQuestions,
      canForceFinalizeSubitemDecision,
      handleFinalizeSubitemDecision,
      quantityUnitLabels,
      editable,
      modifyItemInfo,
      modifyItemProduct,
      modifyItemCalculation,
    ]
  );

  return (
    <DocumentPreviewItems<RequirementPreview_ItemFragment>
      doc={{
        id: requirement.id,
        contractType: "fup",
        isBinding: false,
        agreement: "NONE",
      }}
      fields={fields}
      showCollapseButton={Boolean(expandedStore)}
      shouldRenderItem={item =>
        !item.isHidden &&
        !item.isItemEliminated &&
        !item.isAnyParentItemEliminated
      }
      allDocItems={allDocItems}
      navigateToItem={navigateToItem}
      selectedItemId={selectedItemId}
      isTimeAndMaterialContract={false}
      getItemStyle={getRequirementItemStyle}
      container={container}
      expandedStore={expandedStore}
    />
  );
};

const getRequirementItemStyle = (
  theme: Theme,
  item: RequirementPreview_ItemFragment
): React.CSSProperties | undefined => ({
  color:
    item.decisionOptionElimination ||
    item.isParentDecisionNotPreselected ||
    item.isParentContingencyNotPreselected ||
    (item.decisionIsContingentItem &&
      item.decisionContingentItemPreselection !== true)
      ? theme.palette.grey[600]
      : undefined,
});
